// atlas/AtlasCell.tsx
//
// Renders a single cropped cell from a generated analysis atlas. The atlas image
// is fetched once (authenticated blob) via useAtlas and shared as a blob: URL to
// every AtlasCell, each of which crops its rect with CSS background positioning
// (see atlasCellStyle). This keeps a single network fetch and lets the editorial
// layout drop the user's real photo/eye/lip/hair crops into the right slots.
import React, { useEffect, useState } from 'react';
import { fetchImageBlob } from '../api/imageBlobCache';
import { atlasCellStyle, cellTrim, type AtlasCoords } from './coords';

/**
 * Fetch the atlas image for a result and expose it as a blob: URL.
 * Returns an empty string until loaded (callers render a placeholder).
 */
export function useAtlas(resultImagePath?: string): { atlasUrl: string; loading: boolean } {
  const [atlasUrl, setAtlasUrl] = useState('');
  const [loading, setLoading] = useState(!!resultImagePath);

  useEffect(() => {
    if (!resultImagePath) { setAtlasUrl(''); setLoading(false); return; }
    let cancelled = false;
    let objectUrl = '';
    setLoading(true);
    fetchImageBlob(resultImagePath)
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setAtlasUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setAtlasUrl(''); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [resultImagePath]);

  return { atlasUrl, loading };
}

interface AtlasCellProps {
  atlasUrl: string;
  coords: AtlasCoords;
  cellId: string;
  /** Border radius for the cropped box. Defaults to var(--r-md). */
  radius?: number | string;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

/**
 * A responsive cropped atlas cell. Sizes itself by the cell's aspect ratio and
 * fills its container width; the parent controls the displayed width.
 */
export function AtlasCell({ atlasUrl, coords, cellId, radius = 'var(--r-md)', className, style, title }: AtlasCellProps): React.JSX.Element | null {
  if (!(cellId in coords.cells)) return null;
  const cell = coords.cells[cellId];
  const b = cellTrim(coords);
  const aspect = (cell.w - 2 * b) / (cell.h - 2 * b);
  const cropStyle = atlasUrl ? atlasCellStyle(atlasUrl, coords, cellId) : null;

  return (
    <div
      className={className}
      title={title}
      style={{
        aspectRatio: `${aspect}`,
        width: '100%',
        borderRadius: radius,
        overflow: 'hidden',
        background: cropStyle ? undefined : 'var(--c-sand)',
        ...(cropStyle ?? {}),
        ...style,
      }}
    />
  );
}
